import React, { useState, useEffect } from "react";
import { auth, db, googleProvider } from "../firebase";
import { signInWithEmailAndPassword, signInWithPopup } from "firebase/auth";
import { Link, useNavigate } from "react-router-dom";
import { doc, getDoc, setDoc, addDoc, collection } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";
import AlertError from "./PermissionDenied";
import PasswordResetModal from "./PasswordReset";
import { useUser } from "../userContext";
import { friendlyError } from "../utils/friendlyError";
import cacheManager from "../utils/cacheManager";
import AuthBackground from "./AuthBackground";


export default function Login() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [notice, setNotice] = useState("");
  const [showResetModal, setShowResetModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const { user, loading, getStudioRedirectPath } = useUser();

  useEffect(() => {
    if (loading || !user) return;
    const target = getStudioRedirectPath();
    if (!target) return;
    navigate(target, { replace: true });
  }, [loading, user, getStudioRedirectPath, navigate]);

  useEffect(() => {
    setNotice("");
  }, [email, password]);

  useEffect(() => {
    // Check for email parameter in URL
    const params = new URLSearchParams(window.location.search);
    const emailParam = params.get('email');
    if (emailParam) {
      setEmail(emailParam);
      // Clean up the URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);


  const handleGoogleLogin = async () => {
    try {
      // Purge any stale session data from a previous user before signing in
      localStorage.removeItem('selectedCompanyId');
      cacheManager.clearAllCaches();

      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      setIsProcessing(true);

      // Check if user document exists
      const userDoc = await getDoc(doc(db, "users", user.uid));
      
      if (!userDoc.exists()) {
        // Create new user document
        try {
          const nameParts = user.displayName ? user.displayName.split(' ') : ['', ''];
          const googleFirstName = nameParts[0] || '';
          const googleLastName = nameParts.slice(1).join(' ') || '';

          // Create business document
          const businessRef = await addDoc(collection(db, "workspace"), {
            ownerId: user.uid,
            name: "",
            createdAt: new Date(),
            gameCredits: 0,
          });

          // Create user document
          await setDoc(doc(db, "users", user.uid), {
            uid: user.uid,
            email: user.email,
            firstName: googleFirstName,
            lastName: googleLastName,
            companyId: businessRef.id,
            createdAt: new Date(),
            authProvider: "google",
            emailVerified: true,  // Google emails are pre-verified
            onboardingComplete: false
          });

          // Ensure Firebase Auth user is marked as verified
          if (!auth.currentUser.emailVerified) {
            await auth.currentUser.reload();
          }

          // Send welcome email in background (don't wait)
          try {
            const sendWelcomeEmail = httpsCallable(
              functions,
              'sendWelcomeEmail',
              { region: 'europe-west1' }
            );
            sendWelcomeEmail({ userId: user.uid }).catch(console.error);
          } catch (emailError) {
            console.error('Welcome email error:', emailError);
          }

          sessionStorage.setItem("Auth Token", result._tokenResponse.refreshToken);
          // New Google user: route through onboarding so they don't land on
          // the dashboard with onboardingComplete: false.
          navigate('/studio/setup', { replace: true });
          setIsProcessing(false);
          return;
        } catch (dbError) {
          console.error("Database error:", dbError);
          await auth.signOut();
          setNotice("Failed to create account. Please try again or contact support.");
          setIsProcessing(false);
          return;
        }
      } else {
        // User exists, ensure emailVerified is set to true
        const userData = userDoc.data();
        if (!userData.emailVerified) {
          await setDoc(doc(db, "users", user.uid), {
            ...userData,
            emailVerified: true
          }, { merge: true });
        }
        // Ensure Firebase Auth user is marked as verified
        if (!auth.currentUser.emailVerified) {
          await auth.currentUser.reload();
        }
        sessionStorage.setItem("Auth Token", result._tokenResponse.refreshToken);
      }
    } catch (error) {
      console.error("Error signing in with Google: ", error);
      if (error.email) setEmail(error.email);
      setNotice(friendlyError(error, 'An error occurred during sign in. Please try again.'));
      // Ensure we're signed out in case of error
      try {
        await auth.signOut();
      } catch (signOutError) {
        console.error("Error signing out:", signOutError);
      }
    }
    setIsProcessing(false);
  };

  const loginWithUsernameAndPassword = async (e) => {
    e.preventDefault();
    setIsProcessing(true);

    try {
      // Purge any stale session data from a previous user before signing in
      localStorage.removeItem('selectedCompanyId');
      cacheManager.clearAllCaches();

      const response = await signInWithEmailAndPassword(auth, email, password);
      sessionStorage.setItem("Auth Token", response._tokenResponse.refreshToken);
    } catch (error) {
      setNotice(friendlyError(error, 'Invalid email or password. Please try again.'));
      setIsProcessing(false);
      console.error("Error signing in: ", error);
    }
  };

  return (
    <>
      <div className="auth-viewport admin-theme gch-page">
        <AuthBackground />
        <main className="w-full flex flex-col flex-1 min-h-0 relative z-10">
          {/* Header - fixed height */}
          <div className="flex-shrink-0 flex items-center justify-between p-6 lg:px-10 lg:py-8">
            <a href="/" className="flex items-center gap-3">
              <img src="/transSmilesFlowFavicon.png" alt="SmilesFlow" className="h-8 w-8" />
            </a>
            <div className="text-sm gch-text-secondary">
              Don't have an account?{' '}
              <Link to="/studio/register" className="font-semibold text-[#C45443] hover:opacity-80 transition-opacity">
                Sign up
              </Link>
            </div>
          </div>

          {/* Form Content - only this area scrolls */}
          <div className="flex-1 min-h-0 overflow-y-auto flex items-center justify-center p-6 lg:p-8" data-app-scroll-root>
              <div className="w-full max-w-md mx-auto">
                {/* Hero */}
                <div className="text-center mb-8">
                  <h1 className="text-3xl md:text-4xl gch-heading tracking-tight">
                    Welcome back
                  </h1>
                </div>

                {/* Error */}
                {notice && (
                  <div className="mb-6 p-3 gch-surface rounded-[var(--admin-card-radius)] text-sm font-medium text-red-400 border border-red-500/30" style={{ background: 'rgba(229, 106, 84, 0.15)' }}>
                    {notice}
                  </div>
                )}

                {/* Sign-in Card */}
                <div className="gch-card p-6 sm:p-8">
                  {!showEmailForm ? (
                    <div className="space-y-5">
                      {/* Google Button */}
                      <button
                        onClick={handleGoogleLogin}
                        disabled={isProcessing}
                        className="btn-admin-primary w-full flex items-center justify-center gap-3 disabled:opacity-50"
                      >
                        {isProcessing ? (
                          <div className="flex items-center">
                            <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-3" />
                            Signing in...
                          </div>
                        ) : (
                          <>
                            <svg className="h-5 w-5" viewBox="0 0 24 24">
                              <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                              <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                              <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                              <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                            </svg>
                            Sign in with Google
                          </>
                        )}
                      </button>

                      {/* Divider */}
                      <div className="relative">
                        <div className="absolute inset-0 flex items-center">
                          <div className="w-full border-t border-[var(--admin-border)]" />
                        </div>
                        <div className="relative flex justify-center">
                          <span className="px-3 text-xs font-medium gch-text-muted" style={{ background: 'var(--admin-card-bg)' }}>
                            OR
                          </span>
                        </div>
                      </div>

                      {/* Email option */}
                      <button
                        type="button"
                        onClick={() => setShowEmailForm(true)}
                        className="btn-admin-outline btn-admin-md w-full"
                      >
                        Sign in with email
                      </button>
                    </div>
                  ) : (
                    <form onSubmit={loginWithUsernameAndPassword} className="space-y-5">
                      <div>
                        <label htmlFor="email" className="block text-sm font-semibold leading-6 mb-2 gch-text-primary">
                          Email
                        </label>
                        <input
                          id="email"
                          name="email"
                          type="email"
                          required
                          autoComplete="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className="gch-input-ghost block w-full px-4 py-3 gch-text-primary"
                          placeholder="you@example.com"
                        />
                      </div>

                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <label htmlFor="password" className="block text-sm font-semibold leading-6 gch-text-primary">
                            Password
                          </label>
                          <button
                            type="button"
                            className="text-sm font-medium text-[var(--admin-accent)] hover:opacity-80 transition-opacity"
                            onClick={() => setShowResetModal(true)}
                          >
                            Forgot password?
                          </button>
                        </div>
                        <input
                          id="password"
                          name="password"
                          type="password"
                          required
                          autoComplete="current-password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className="gch-input-ghost block w-full px-4 py-3 gch-text-primary"
                          placeholder="Enter your password"
                        />
                      </div>

                      <button
                        type="submit"
                        disabled={isProcessing}
                        className="btn-admin-primary btn-admin-md w-full inline-flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isProcessing ? (
                          <div className="flex items-center">
                            <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent mr-3" />
                            Signing in...
                          </div>
                        ) : (
                          'Sign in'
                        )}
                      </button>

                      <div className="text-center">
                        <button
                          type="button"
                          onClick={() => setShowEmailForm(false)}
                          className="text-sm font-medium gch-text-muted hover:opacity-80 transition-opacity"
                        >
                          ← Back to all sign-in options
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </main>
      </div>

      <PasswordResetModal show={showResetModal} onClose={() => setShowResetModal(false)} auth={auth} />
    </>
  );
}
